import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  FormControl,
  HStack,
  FormLabel,
  Input,
  FormHelperText,
  FormErrorMessage,
  Box,
  Table,
  Tr,
  Td,
  CircularProgress,
  SimpleGrid,
  Flex,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import IconSelectionField, { DEFAULT_ICON } from '../fields/IconSelectionField';
import { Field, Form, Formik } from 'formik';
import Collection from 'backend/models/Collection';
import { useRef, useState } from 'react';
import * as Yup from 'yup';
import { createCollection, fetchCollections, updateCollection } from 'features/collections/thunks';
import { useCurrentJWT } from 'lib/AuthUtils';
import { useDispatch, useSelector } from 'react-redux';
import { currentOrganizationSelector } from 'features/me/meSlice';
import { AppDispatch } from 'components/store';
import { useToast } from '@chakra-ui/react';
import { BiHelpCircle, BiPencil } from 'react-icons/bi';


type CollectionModalProps = {
  isOpen: boolean,
  onClose: () => void,
  collection?: Collection
}

function CollectionModal({ isOpen, onClose, collection }: CollectionModalProps) {

  //const { isOpen, onClose } = useDisclosure();
  const formRef = useRef<any>();
  const jwt = useCurrentJWT();
  const organization_id = useSelector(currentOrganizationSelector());
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setSaving] = useState(false);
  const toast = useToast();
  const isEdit = !!collection;
  const initialValues:Collection = collection ?? {
    id: '',
    icon: DEFAULT_ICON,
    name: '',
    order: undefined
  };

  const formSchema = Yup.object().shape({
    name: Yup.string()
      .required('Collection name is required.'),
    id: Yup.string()
      .required('Collection ID is required.')
    //.matches(organizationIdRegex, 'Organization ID can only contain letters, numbers, hyphens, and underscores.')
  });



  return (
    <>

      <Modal
        isOpen={isOpen}
        size='xl'
        onClose={onClose}>
        <ModalOverlay />
        <ModalContent minW={800}>
          <ModalHeader>{isEdit ? `Edit Collection "${collection.name}"` : 'Create new Collection'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik<Collection>
              innerRef={formRef}
              validationSchema={formSchema}
              enableReinitialize={true}
              initialValues={initialValues}
              onSubmit={async (collection: Collection, actions) => {
                //console.error(JSON.stringify(values));
                let toastMessage = '';
                try {
                  setSaving(true);
                  if (isEdit) {
                    await dispatch(updateCollection({ jwt, organization_id, collection }));
                    toastMessage = `Collection "${collection.name}" successfully updated.`
                  } else {
                    await dispatch(createCollection({ jwt, organization_id, body: collection }));
                    toastMessage = `Collection "${collection.name}" successfully created.`
                  }
                  
                  await dispatch(fetchCollections({ jwt, organization_id }));
                  toast({
                    title: 'Success',
                    description: toastMessage,
                    status: 'success',
                    isClosable: true,
                  });
                  onClose();
                } catch (error) {
                  console.error(error);
                  //noop
                } finally {
                  setSaving(false);
                }

                //dispatch(setForm1(values));
                //setActiveStep(activeStep => activeStep + 1);
              }}
            >
              {(props) => (
                <Form>
                  <SimpleGrid columns={2} spacing={3}>
                    <Field name='name'>
                      {({ field, form }) => (
                        <FormControl minH='101px' mb={5} isInvalid={form.errors.name && form.touched.name} isRequired>
                          <FormLabel>Collection Name</FormLabel>
                          <Input {...field} placeholder='Blog Posts' />
                          <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name='id'>
                      {({ field, form }) => (
                        <FormControl minH='101px' mb={5} isInvalid={form.errors.id && form.touched.id} isRequired>
                          <FormLabel>ID</FormLabel>
                          <Input {...field} placeholder='blog-posts' />
                          <FormErrorMessage>{form.errors.id}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name='icon'>
                      {({ field, form }) => (
                        <FormControl minH='101px' mb={5}>
                          <FormLabel>Icon</FormLabel>
                          <IconSelectionField {...field} />
                          <FormErrorMessage></FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name='order'>
                      {({ field, form }) => (
                        <FormControl minH='101px' mb={5} isInvalid={form.errors.order && form.touched.order}>
                          <FormLabel>
                            <Flex>
                              Order
                              <Tooltip hasArrow label="Those with lower order value will be displayed first." placement='top'>
                                <Box ml={1} _hover={{ cursor: 'pointer' }}>
                                  <Icon as={BiHelpCircle} mb={1} />
                                </Box>
                              </Tooltip>
                            </Flex>
                          </FormLabel>
                          <Input type='number' {...field} placeholder='e.g., 10' />
                          <FormErrorMessage>{form.errors.order}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                  </SimpleGrid>
                </Form>
              )}
            </Formik>


          </ModalBody>

          <ModalFooter>
            <Box mr={3}>
              {isSaving && <CircularProgress isIndeterminate size='14px' color='app.primary' />}
            </Box>
            <Button mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              bg='buttonBg.primary'
              color='button.primary'
              leftIcon={isEdit ? <BiPencil /> : <FaPlus />}
              _hover={{ bg: 'buttonBg.primary' }}
              onClick={() => formRef?.current?.submitForm()}
            >{isEdit ? 'Update' : 'Create'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default CollectionModal;
